import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { getProjects, getBalanceSheets, createBalanceSheet, updateBalanceSheet, deleteBalanceSheet } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Save, Trash2, Plus, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const BalanceSheet = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = searchParams.get('project') || '';
  
  const [projects, setProjects] = useState([]);
  const [balanceSheets, setBalanceSheets] = useState([]);
  const [selectedProject, setSelectedProject] = useState(projectId);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentSheet, setCurrentSheet] = useState(null);
  const [formData, setFormData] = useState({
    year: new Date().getFullYear(),
    // Attivo
    immobilizzazioni_immateriali: 0,
    immobilizzazioni_materiali: 0,
    immobilizzazioni_finanziarie: 0,
    rimanenze: 0,
    crediti_commerciali: 0,
    disponibilita_liquide: 0,
    altri_crediti: 0,
    // Passivo
    capitale_sociale: 0,
    riserve: 0,
    utile_perdita: 0,
    debiti_finanziari_mlungo: 0,
    debiti_finanziari_breve: 0,
    debiti_commerciali: 0,
    altri_debiti: 0,
    tfr: 0,
    // Conto Economico
    ricavi: 0,
    costi_materie_prime: 0,
    costi_servizi: 0,
    costi_personale: 0,
    ammortamenti: 0,
    altri_costi: 0,
    oneri_finanziari: 0,
    proventi_finanziari: 0,
    imposte: 0
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchBalanceSheets();
    }
  }, [selectedProject]);

  useEffect(() => {
    if (id && balanceSheets.length > 0) {
      const sheet = balanceSheets.find(bs => bs.id === id);
      if (sheet) {
        setCurrentSheet(sheet);
        setFormData({
          year: sheet.year,
          ...sheet.data
        });
      }
    }
  }, [id, balanceSheets]);

  const fetchProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
      if (!selectedProject && response.data.length > 0) {
        setSelectedProject(response.data[0].id);
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchBalanceSheets = async () => {
    try {
      const response = await getBalanceSheets(selectedProject);
      setBalanceSheets(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSave = async () => {
    if (!selectedProject) {
      toast.error('Seleziona un progetto');
      return;
    }

    setSaving(true);
    try {
      const data = {
        project_id: selectedProject,
        year: formData.year,
        immobilizzazioni_immateriali: parseFloat(formData.immobilizzazioni_immateriali) || 0,
        immobilizzazioni_materiali: parseFloat(formData.immobilizzazioni_materiali) || 0,
        immobilizzazioni_finanziarie: parseFloat(formData.immobilizzazioni_finanziarie) || 0,
        rimanenze: parseFloat(formData.rimanenze) || 0,
        crediti_commerciali: parseFloat(formData.crediti_commerciali) || 0,
        disponibilita_liquide: parseFloat(formData.disponibilita_liquide) || 0,
        altri_crediti: parseFloat(formData.altri_crediti) || 0,
        capitale_sociale: parseFloat(formData.capitale_sociale) || 0,
        riserve: parseFloat(formData.riserve) || 0,
        utile_perdita: parseFloat(formData.utile_perdita) || 0,
        debiti_finanziari_mlungo: parseFloat(formData.debiti_finanziari_mlungo) || 0,
        debiti_finanziari_breve: parseFloat(formData.debiti_finanziari_breve) || 0,
        debiti_commerciali: parseFloat(formData.debiti_commerciali) || 0,
        altri_debiti: parseFloat(formData.altri_debiti) || 0,
        tfr: parseFloat(formData.tfr) || 0,
        ricavi: parseFloat(formData.ricavi) || 0,
        costi_materie_prime: parseFloat(formData.costi_materie_prime) || 0,
        costi_servizi: parseFloat(formData.costi_servizi) || 0,
        costi_personale: parseFloat(formData.costi_personale) || 0,
        ammortamenti: parseFloat(formData.ammortamenti) || 0,
        altri_costi: parseFloat(formData.altri_costi) || 0,
        oneri_finanziari: parseFloat(formData.oneri_finanziari) || 0,
        proventi_finanziari: parseFloat(formData.proventi_finanziari) || 0,
        imposte: parseFloat(formData.imposte) || 0
      };

      let response;
      if (currentSheet) {
        response = await updateBalanceSheet(currentSheet.id, data);
      } else {
        response = await createBalanceSheet(data);
      }
      
      setCurrentSheet(response.data);
      fetchBalanceSheets();
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentSheet || !window.confirm('Sei sicuro di voler eliminare questa analisi?')) return;
    
    try {
      await deleteBalanceSheet(currentSheet.id);
      setCurrentSheet(null);
      setFormData({
        year: new Date().getFullYear(),
        immobilizzazioni_immateriali: 0,
        immobilizzazioni_materiali: 0,
        immobilizzazioni_finanziarie: 0,
        rimanenze: 0,
        crediti_commerciali: 0,
        disponibilita_liquide: 0,
        altri_crediti: 0,
        capitale_sociale: 0,
        riserve: 0,
        utile_perdita: 0,
        debiti_finanziari_mlungo: 0,
        debiti_finanziari_breve: 0,
        debiti_commerciali: 0,
        altri_debiti: 0,
        tfr: 0,
        ricavi: 0,
        costi_materie_prime: 0,
        costi_servizi: 0,
        costi_personale: 0,
        ammortamenti: 0,
        altri_costi: 0,
        oneri_finanziari: 0,
        proventi_finanziari: 0,
        imposte: 0
      });
      fetchBalanceSheets();
      toast.success(t('common.success'));
      navigate(`/balance-sheet?project=${selectedProject}`);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleNewSheet = () => {
    setCurrentSheet(null);
    setFormData({
      year: new Date().getFullYear(),
      immobilizzazioni_immateriali: 0,
      immobilizzazioni_materiali: 0,
      immobilizzazioni_finanziarie: 0,
      rimanenze: 0,
      crediti_commerciali: 0,
      disponibilita_liquide: 0,
      altri_crediti: 0,
      capitale_sociale: 0,
      riserve: 0,
      utile_perdita: 0,
      debiti_finanziari_mlungo: 0,
      debiti_finanziari_breve: 0,
      debiti_commerciali: 0,
      altri_debiti: 0,
      tfr: 0,
      ricavi: 0,
      costi_materie_prime: 0,
      costi_servizi: 0,
      costi_personale: 0,
      ammortamenti: 0,
      altri_costi: 0,
      oneri_finanziari: 0,
      proventi_finanziari: 0,
      imposte: 0
    });
  };

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const renderIndexCard = (label, value, suffix = '', trend = null) => {
    const getTrendIcon = () => {
      if (trend === 'up') return <TrendingUp size={16} className="text-green-500" />;
      if (trend === 'down') return <TrendingDown size={16} className="text-red-500" />;
      return <Minus size={16} className="text-muted-foreground" />;
    };

    return (
      <div className="p-4 rounded-xl bg-muted/30 border border-border/50">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm text-muted-foreground">{label}</span>
          {getTrendIcon()}
        </div>
        <p className="text-2xl font-bold number-format">
          {typeof value === 'number' ? value.toLocaleString('it-IT', { maximumFractionDigits: 2 }) : value}
          {suffix && <span className="text-sm font-normal text-muted-foreground ml-1">{suffix}</span>}
        </p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="balance-sheet-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{t('balance.title')}</h1>
            <p className="text-muted-foreground mt-1">
              {currentSheet ? `Anno ${currentSheet.year}` : 'Nuova analisi'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleNewSheet} data-testid="new-sheet-btn">
            <Plus size={16} className="mr-2" />
            Nuovo
          </Button>
          {currentSheet && (
            <Button variant="outline" onClick={handleDelete} className="text-destructive" data-testid="delete-sheet-btn">
              <Trash2 size={16} className="mr-2" />
              {t('common.delete')}
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-violet-600 to-indigo-600" data-testid="save-btn">
            <Save size={16} className="mr-2" />
            {saving ? t('common.loading') : t('common.save')}
          </Button>
        </div>
      </div>

      {/* Project & Year Selection */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-6">
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Progetto</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger data-testid="project-select">
                  <SelectValue placeholder="Seleziona progetto" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name} - {p.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('balance.year')}</Label>
              <Input
                type="number"
                value={formData.year}
                onChange={(e) => updateField('year', parseInt(e.target.value))}
                min={2000}
                max={2100}
                data-testid="year-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Analisi Salvate</Label>
              <Select 
                value={currentSheet?.id || ''} 
                onValueChange={(value) => {
                  const sheet = balanceSheets.find(bs => bs.id === value);
                  if (sheet) {
                    setCurrentSheet(sheet);
                    setFormData({ year: sheet.year, ...sheet.data });
                  }
                }}
              >
                <SelectTrigger data-testid="saved-sheets-select">
                  <SelectValue placeholder="Seleziona anno" />
                </SelectTrigger>
                <SelectContent>
                  {balanceSheets.map(bs => (
                    <SelectItem key={bs.id} value={bs.id}>Anno {bs.year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Entry Tabs */}
      <Tabs defaultValue="assets" className="space-y-6">
        <TabsList className="bg-muted/50 p-1 rounded-xl w-full sm:w-auto">
          <TabsTrigger value="assets" className="rounded-lg" data-testid="tab-assets">{t('balance.assets')}</TabsTrigger>
          <TabsTrigger value="liabilities" className="rounded-lg" data-testid="tab-liabilities">{t('balance.liabilities')}</TabsTrigger>
          <TabsTrigger value="income" className="rounded-lg" data-testid="tab-income">{t('balance.incomeStatement')}</TabsTrigger>
          <TabsTrigger value="indices" className="rounded-lg" data-testid="tab-indices">{t('indices.title')}</TabsTrigger>
        </TabsList>

        {/* Assets Tab */}
        <TabsContent value="assets">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>{t('balance.assets')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold mb-4">{t('balance.fixedAssets')}</h4>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>{t('balance.intangible')}</Label>
                    <Input
                      type="number"
                      value={formData.immobilizzazioni_immateriali}
                      onChange={(e) => updateField('immobilizzazioni_immateriali', e.target.value)}
                      data-testid="input-immateriali"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('balance.tangible')}</Label>
                    <Input
                      type="number"
                      value={formData.immobilizzazioni_materiali}
                      onChange={(e) => updateField('immobilizzazioni_materiali', e.target.value)}
                      data-testid="input-materiali"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('balance.financial')}</Label>
                    <Input
                      type="number"
                      value={formData.immobilizzazioni_finanziarie}
                      onChange={(e) => updateField('immobilizzazioni_finanziarie', e.target.value)}
                      data-testid="input-finanziarie"
                    />
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-4">{t('balance.currentAssets')}</h4>
                <div className="grid sm:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>{t('balance.inventory')}</Label>
                    <Input
                      type="number"
                      value={formData.rimanenze}
                      onChange={(e) => updateField('rimanenze', e.target.value)}
                      data-testid="input-rimanenze"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('balance.receivables')}</Label>
                    <Input
                      type="number"
                      value={formData.crediti_commerciali}
                      onChange={(e) => updateField('crediti_commerciali', e.target.value)}
                      data-testid="input-crediti"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('balance.cash')}</Label>
                    <Input
                      type="number"
                      value={formData.disponibilita_liquide}
                      onChange={(e) => updateField('disponibilita_liquide', e.target.value)}
                      data-testid="input-liquidita"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('balance.otherReceivables')}</Label>
                    <Input
                      type="number"
                      value={formData.altri_crediti}
                      onChange={(e) => updateField('altri_crediti', e.target.value)}
                      data-testid="input-altri-crediti"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Liabilities Tab */}
        <TabsContent value="liabilities">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>{t('balance.liabilities')} & {t('balance.equity')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold mb-4">{t('balance.equity')}</h4>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>{t('balance.shareCapital')}</Label>
                    <Input
                      type="number"
                      value={formData.capitale_sociale}
                      onChange={(e) => updateField('capitale_sociale', e.target.value)}
                      data-testid="input-capitale"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('balance.reserves')}</Label>
                    <Input
                      type="number"
                      value={formData.riserve}
                      onChange={(e) => updateField('riserve', e.target.value)}
                      data-testid="input-riserve"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('balance.profitLoss')}</Label>
                    <Input
                      type="number"
                      value={formData.utile_perdita}
                      onChange={(e) => updateField('utile_perdita', e.target.value)}
                      data-testid="input-utile"
                    />
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-4">{t('balance.liabilities')}</h4>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>{t('balance.ltDebt')}</Label>
                    <Input
                      type="number"
                      value={formData.debiti_finanziari_mlungo}
                      onChange={(e) => updateField('debiti_finanziari_mlungo', e.target.value)}
                      data-testid="input-debiti-ml"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('balance.stDebt')}</Label>
                    <Input
                      type="number"
                      value={formData.debiti_finanziari_breve}
                      onChange={(e) => updateField('debiti_finanziari_breve', e.target.value)}
                      data-testid="input-debiti-breve"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('balance.payables')}</Label>
                    <Input
                      type="number"
                      value={formData.debiti_commerciali}
                      onChange={(e) => updateField('debiti_commerciali', e.target.value)}
                      data-testid="input-debiti-comm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('balance.otherPayables')}</Label>
                    <Input
                      type="number"
                      value={formData.altri_debiti}
                      onChange={(e) => updateField('altri_debiti', e.target.value)}
                      data-testid="input-altri-debiti"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('balance.tfr')}</Label>
                    <Input
                      type="number"
                      value={formData.tfr}
                      onChange={(e) => updateField('tfr', e.target.value)}
                      data-testid="input-tfr"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Income Statement Tab */}
        <TabsContent value="income">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>{t('balance.incomeStatement')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t('balance.revenue')}</Label>
                  <Input
                    type="number"
                    value={formData.ricavi}
                    onChange={(e) => updateField('ricavi', e.target.value)}
                    data-testid="input-ricavi"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('balance.rawMaterials')}</Label>
                  <Input
                    type="number"
                    value={formData.costi_materie_prime}
                    onChange={(e) => updateField('costi_materie_prime', e.target.value)}
                    data-testid="input-materie"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('balance.services')}</Label>
                  <Input
                    type="number"
                    value={formData.costi_servizi}
                    onChange={(e) => updateField('costi_servizi', e.target.value)}
                    data-testid="input-servizi"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('balance.personnel')}</Label>
                  <Input
                    type="number"
                    value={formData.costi_personale}
                    onChange={(e) => updateField('costi_personale', e.target.value)}
                    data-testid="input-personale"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('balance.depreciation')}</Label>
                  <Input
                    type="number"
                    value={formData.ammortamenti}
                    onChange={(e) => updateField('ammortamenti', e.target.value)}
                    data-testid="input-ammortamenti"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('balance.otherCosts')}</Label>
                  <Input
                    type="number"
                    value={formData.altri_costi}
                    onChange={(e) => updateField('altri_costi', e.target.value)}
                    data-testid="input-altri-costi"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('balance.interestExpense')}</Label>
                  <Input
                    type="number"
                    value={formData.oneri_finanziari}
                    onChange={(e) => updateField('oneri_finanziari', e.target.value)}
                    data-testid="input-oneri"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('balance.interestIncome')}</Label>
                  <Input
                    type="number"
                    value={formData.proventi_finanziari}
                    onChange={(e) => updateField('proventi_finanziari', e.target.value)}
                    data-testid="input-proventi"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('balance.taxes')}</Label>
                  <Input
                    type="number"
                    value={formData.imposte}
                    onChange={(e) => updateField('imposte', e.target.value)}
                    data-testid="input-imposte"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Indices Tab */}
        <TabsContent value="indices">
          {currentSheet?.indices ? (
            <div className="space-y-6">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle>Situazione Patrimoniale</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {renderIndexCard('Totale Attivo', currentSheet.indices.totale_attivo, '€')}
                    {renderIndexCard('Attivo Fisso', currentSheet.indices.totale_attivo_fisso, '€')}
                    {renderIndexCard('Attivo Circolante', currentSheet.indices.totale_attivo_circolante, '€')}
                    {renderIndexCard('Patrimonio Netto', currentSheet.indices.patrimonio_netto, '€')}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle>{t('indices.profitability')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {renderIndexCard('EBITDA', currentSheet.indices.ebitda, '€', currentSheet.indices.ebitda > 0 ? 'up' : 'down')}
                    {renderIndexCard('EBIT', currentSheet.indices.ebit, '€', currentSheet.indices.ebit > 0 ? 'up' : 'down')}
                    {renderIndexCard('Risultato Netto', currentSheet.indices.risultato_netto, '€', currentSheet.indices.risultato_netto > 0 ? 'up' : 'down')}
                    {renderIndexCard('EBITDA Margin', currentSheet.indices.ebitda_margin, '%')}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle>Indici di Redditività</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {renderIndexCard(t('indices.roe'), currentSheet.indices.roe, '%', currentSheet.indices.roe > 0 ? 'up' : 'down')}
                    {renderIndexCard(t('indices.roa'), currentSheet.indices.roa, '%', currentSheet.indices.roa > 0 ? 'up' : 'down')}
                    {renderIndexCard(t('indices.roi'), currentSheet.indices.roi, '%', currentSheet.indices.roi > 0 ? 'up' : 'down')}
                    {renderIndexCard(t('indices.ros'), currentSheet.indices.ros, '%', currentSheet.indices.ros > 0 ? 'up' : 'down')}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle>{t('indices.liquidity')} & {t('indices.leverage')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {renderIndexCard(t('indices.currentRatio'), currentSheet.indices.current_ratio, '', currentSheet.indices.current_ratio >= 1.5 ? 'up' : 'down')}
                    {renderIndexCard(t('indices.quickRatio'), currentSheet.indices.quick_ratio, '', currentSheet.indices.quick_ratio >= 1 ? 'up' : 'down')}
                    {renderIndexCard(t('indices.debtEquity'), currentSheet.indices.debt_equity, '', currentSheet.indices.debt_equity < 2 ? 'up' : 'down')}
                    {renderIndexCard(t('indices.pfn'), currentSheet.indices.pfn, '€')}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="border-0 shadow-lg">
              <CardContent className="py-12 text-center text-muted-foreground">
                <p>Salva i dati di bilancio per visualizzare gli indici calcolati</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BalanceSheet;
